/**
 * 平台 · 拆分（与旧 PHP 一致：表单操作为主，记录为次）
 *
 * - TP手续费转给用户：查询账号 + 从 tp_handling_fee_pool 发放
 * - 拆分：批量 SP→ORB 分裂触发
 * - 分裂记录：仅作审计列表
 *
 * /parity/uo_config 对齐线网 `admin/uo.config/index.html`：2 Tab、TP tab 含查询/发放、拆 tab 底部分裂记录表
 */
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  ConfigProvider,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Tabs,
  Tooltip,
  message,
  theme,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { TOKEN_IDS, TOKEN_META, type TokenId } from '@uo/shared';

interface SplitRow {
  id: string;
  user_id: number;
  pool: string;
  input: string;
  output: string;
  ratio: string;
  create_time: string;
}

interface TriggerPreview {
  dry_run: boolean;
  pool: string;
  ratio: string;
  affected_users: number;
  total_input: string;
  total_output: string;
}

interface TpPoolPreview {
  xian: boolean;
  user_id: number;
  username: string;
  asset: string;
  amount: string;
  pool_before: string;
  pool_after: string;
  would_apply: boolean;
}

/** GET /api/admin/split-log/tp-fee-pool */
interface TpFeePoolData {
  remaining: string;
  uo_config_remaining?: string;
  display_source?: 'uo_config' | 'php_mirror';
}

const readRowGrey: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 12,
  padding: '10px 12px',
  background: '#f5f5f5',
  border: '1px solid #e8e8e8',
  borderRadius: 2,
  fontSize: 14,
};

const parityFormLabel = {
  labelCol: { flex: '0 0 200px' as const },
  wrapperCol: { flex: '1 1 auto' as const },
  labelAlign: 'right' as const,
};

export function SplitLogPage() {
  const { pathname } = useLocation();
  const isParityUoConfig = pathname === '/parity/uo_config';
  const { token } = theme.useToken();
  const [userId, setUserId] = useState<string>('');
  const [pool, setPool] = useState<string | undefined>();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const parityMenuOk =
    isParityUoConfig &&
    (isSuper || hasMenu('parity.uo_config') || hasNode('parity.uo_config'));
  const canList = hasNode('platform.split.list') || parityMenuOk;
  const canTrigger = hasNode('platform.split.trigger') || parityMenuOk;

  // 线网 uo.config 第二 tab 仅「拆分倍数」无池子选择；Node 触发固定用 sp1（与旧站默认一致）
  useEffect(() => {
    if (isParityUoConfig) setTriggerPool('sp1');
  }, [isParityUoConfig]);

  // --- TP 手续费表单子标签 ---
  const { data: tpPool, refetch: refetchTpPool } = useQuery({
    queryKey: ['admin', 'split', 'tp-fee-pool'],
    queryFn: () => api.get<TpFeePoolData>('/api/admin/split-log/tp-fee-pool'),
    enabled: canList,
  });
  const [tpForm] = Form.useForm<{
    username: string;
    amount: string;
    asset: TokenId;
  }>();
  const [lookedUp, setLookedUp] = useState<{
    id: number;
    username: string;
    nickname: string | null;
  } | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [tpPreview, setTpPreview] = useState<TpPoolPreview | null>(null);
  const [tpPreviewOpen, setTpPreviewOpen] = useState(false);
  const [tpPreviewLoading, setTpPreviewLoading] = useState(false);
  const [tpExecuteLoading, setTpExecuteLoading] = useState(false);

  async function runLookup() {
    const u = (tpForm.getFieldValue('username') ?? '').trim();
    if (!u) {
      message.warning('请输入用户登录账号');
      return;
    }
    setLookupLoading(true);
    setLookedUp(null);
    try {
      const res = await api.get<{
        id: number;
        username: string;
        nickname: string | null;
        status: string;
      }>('/api/admin/split-log/user-by-username', { username: u });
      setLookedUp(res);
      message.success(`已找到用户 ID ${res.id}（${res.status}）`);
    } catch (e) {
      setLookedUp(null);
      message.error(e instanceof ApiError ? e.message : '未找到该账号');
    } finally {
      setLookupLoading(false);
    }
  }

  async function runTpPreview() {
    if (isParityUoConfig && !lookedUp) {
      message.warning('请先点击「查询」确认用户');
      return;
    }
    const v = await tpForm.validateFields();
    setTpPreviewLoading(true);
    setTpPreview(null);
    try {
      const res = (await api.post<TpPoolPreview>('/api/admin/split-log/tp-fee-distribute', {
        ...v,
        amount: v.amount.trim(),
        username: v.username.trim(),
        xian: true,
        note: 'TP手续费发放',
      })) as TpPoolPreview;
      setTpPreview(res);
      setTpPreviewOpen(true);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '预览失败');
    } finally {
      setTpPreviewLoading(false);
    }
  }

  async function executeTpDistribute() {
    const v = await tpForm.validateFields();
    setTpExecuteLoading(true);
    try {
      await api.post('/api/admin/split-log/tp-fee-distribute', {
        ...v,
        amount: v.amount.trim(),
        username: v.username.trim(),
        xian: false,
        note: 'TP手续费发放',
      });
      message.success('发放成功');
      setTpPreviewOpen(false);
      setTpPreview(null);
      void refetchTpPool();
      setLookedUp(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'split-log'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '发放失败');
    } finally {
      setTpExecuteLoading(false);
    }
  }

  // --- 批量分裂（子标签「拆分」）---
  const [triggerPool, setTriggerPool] = useState<'sp1' | 'sp2' | 'sp3'>('sp1');
  const [triggerRatio, setTriggerRatio] = useState<string>('0.1');
  const [preview, setPreview] = useState<TriggerPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [level1Retain, setLevel1Retain] = useState('20');
  const [level2Retain, setLevel2Retain] = useState('200');
  const [netWeight1, setNetWeight1] = useState('10');
  const [netWeight2, setNetWeight2] = useState('100');
  const [guideCoins, setGuideCoins] = useState('1000000');
  const [guideSalesLoading, setGuideSalesLoading] = useState(false);
  const [guidePreview, setGuidePreview] = useState<{
    source?: string;
    unit_price?: string;
    sum_uo?: string | number;
    ap?: string | number;
    bj?: string | number;
    ymc?: string | number;
    interval?: string;
    interval1?: string;
    interval2?: string;
    interval_people1?: string | number;
    interval_people2?: string | number;
    net_weight_interval1?: string;
    net_weight_interval2?: string;
    net_weight_people1?: string | number;
    net_weight_people2?: string | number;
    trigger_count?: number;
    trigger_sum_uo?: string;
    trigger_sum_ap?: string;
  } | null>(null);

  const { data: cycleState, refetch: refetchCycle } = useQuery({
    queryKey: ['admin', 'split', 'cycle-state'],
    queryFn: () =>
      api.get<{
        phase: string;
        open_guide_sell_uo: string;
        can_execute_split: boolean;
        can_trigger_guide_sales: boolean;
        pending_ratio: string | null;
        split_preview: TriggerPreview | null;
        guide_sales_calc?: typeof guidePreview;
      }>('/api/admin/split-log/cycle/state'),
    enabled: canList,
    refetchInterval: 30_000,
  });

  async function runSplitPreview() {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = (await api.post('/api/admin/split-log/cycle/split-preview', {
        pool: triggerPool,
        ratio: triggerRatio,
      })) as TriggerPreview & { phase?: string };
      setPreview(res);
      setConfirmVisible(true);
      void refetchCycle();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '预览失败');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function runGuideSalesPreview() {
    setGuideSalesLoading(true);
    try {
      const res = (await api.post('/api/admin/split-log/cycle/guide-sales-preview', {
        level1: level1Retain,
        level2: level2Retain,
        net_weight1: netWeight1,
        net_weight2: netWeight2,
        coins: guideCoins,
      })) as typeof guidePreview;
      setGuidePreview(res);
      void refetchCycle();
      message.success(
        `计算完成（${res?.source === 'php_live' ? '线网' : '镜像'}）：过线 ${res?.interval_people1}/${res?.interval_people2} · 出局 ${res?.net_weight_people1}/${res?.net_weight_people2} · 待挂卖 ${res?.trigger_count ?? 0} 户`,
      );
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '指导销售测算失败');
    } finally {
      setGuideSalesLoading(false);
    }
  }

  async function triggerGuideSales() {
    setGuideSalesLoading(true);
    try {
      const res = (await api.post('/api/admin/split-log/cycle/guide-sales-trigger', {
        level1: level1Retain,
        level2: level2Retain,
        net_weight1: netWeight1,
        net_weight2: netWeight2,
        coins: guideCoins,
        dry_run: false,
      })) as { listed_count: number; skipped_count: number };
      message.success(`指导销售已挂卖 ${res.listed_count} 单（跳过 ${res.skipped_count}）`);
      void refetchCycle();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '指导销售触发失败');
    } finally {
      setGuideSalesLoading(false);
    }
  }

  async function executeTrigger() {
    setExecuting(true);
    try {
      const res = (await api.post('/api/admin/split-log/cycle/split-execute', {
        pool: triggerPool,
        ratio: triggerRatio,
      })) as TriggerPreview;
      message.success(
        `拆分完成：${res.affected_users} 用户，投入 ${res.total_input}，产出 ${res.total_output}`,
      );
      setConfirmVisible(false);
      setPreview(null);
      void refetchCycle();
      void qc.invalidateQueries({ queryKey: ['admin', 'split-log'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '执行失败');
    } finally {
      setExecuting(false);
    }
  }

  const logColumns = [
    { title: 'ID', dataIndex: 'id', width: 120 },
    { title: '时间', dataIndex: 'create_time', width: 170 },
    { title: '用户', dataIndex: 'user_id', width: 90 },
    { title: '池', dataIndex: 'pool', width: 80 },
    { title: '投入', dataIndex: 'input', width: 140 },
    { title: '产出', dataIndex: 'output', width: 140 },
    { title: '比例', dataIndex: 'ratio', width: 120 },
  ];

  const logTableEl = canList ? (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="池"
          style={{ width: 120 }}
          allowClear
          value={pool}
          onChange={setPool}
          options={[
            { value: 'sp1', label: 'SP1' },
            { value: 'sp2', label: 'SP2' },
            { value: 'sp3', label: 'SP3' },
          ]}
        />
        <Input
          placeholder="用户 ID"
          style={{ width: 140 }}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          allowClear
        />
      </Space>
      <DataTable<SplitRow>
        queryKey={['admin', 'split-log', pool ?? '', userId, isParityUoConfig ? 'parity' : 'plat']}
        endpoint="/api/admin/split-log"
        extraParams={{
          user_id: userId.trim() && !Number.isNaN(Number(userId)) ? Number(userId) : undefined,
          pool,
        }}
        columns={logColumns}
        rowKey="id"
        scroll={{ x: 1000 }}
      />
    </>
  ) : null;

  const tabGreenTheme = {
    components: {
      Tabs: {
        inkBarColor: '#16a34a',
        itemActiveColor: '#16a34a',
        itemSelectedColor: '#16a34a',
        itemHoverColor: '#16a34a',
        titleFontSize: 15,
      },
    },
  } as const;

  const sharedTpModal = (
    <Modal
      title="确认从 TP 手续费池发放"
      open={tpPreviewOpen}
      onCancel={() => setTpPreviewOpen(false)}
      onOk={() => void executeTpDistribute()}
      okText="确认发放"
      okButtonProps={{ danger: true, loading: tpExecuteLoading }}
    >
      {tpPreview && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="用户">
            {tpPreview.username}（ID: {tpPreview.user_id}）
          </Descriptions.Item>
          <Descriptions.Item label="币种">{tpPreview.asset.toUpperCase()}</Descriptions.Item>
          <Descriptions.Item label="数量">
            <span style={{ fontFamily: 'monospace' }}>{tpPreview.amount}</span>
          </Descriptions.Item>
          <Descriptions.Item label="池扣减前">{tpPreview.pool_before}</Descriptions.Item>
          <Descriptions.Item label="池扣减后">{tpPreview.pool_after}</Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );

  const splitConfirmModal = (
    <Modal
      title="确认执行分裂"
      open={confirmVisible}
      onCancel={() => setConfirmVisible(false)}
      onOk={() => void executeTrigger()}
      okText="确认执行"
      okButtonProps={{ danger: true, loading: executing }}
      cancelText="取消"
    >
      {preview && (
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="分裂池">{preview.pool.toUpperCase()}</Descriptions.Item>
          <Descriptions.Item label="比例">{preview.ratio}</Descriptions.Item>
          <Descriptions.Item label="影响用户数">{preview.affected_users}</Descriptions.Item>
          <Descriptions.Item label="预计总投入">
            <span style={{ fontFamily: 'monospace' }}>{preview.total_input}</span>
          </Descriptions.Item>
          <Descriptions.Item label="预计总产出">
            <span style={{ fontFamily: 'monospace' }}>{preview.total_output}</span>
          </Descriptions.Item>
        </Descriptions>
      )}
      <Alert
        type="error"
        message="此操作不可逆，请确认后再执行"
        style={{ marginTop: 12 }}
        showIcon
      />
    </Modal>
  );

  if (isParityUoConfig) {
    return (
      <div>
        <ConfigProvider theme={tabGreenTheme}>
          <div
            className="parity-uo-config-page"
            style={{ background: '#fff', minHeight: 200, paddingBottom: 8 }}
          >
            <style>{`
              .parity-uo-config-page .ant-tabs-top > .ant-tabs-nav { margin-bottom: 16px; }
              .parity-uo-config-page .ant-tabs-tab-active .ant-tabs-tab-btn { font-weight: 600; }
            `}</style>
            <Tabs
              type="line"
              defaultActiveKey="tp"
              items={[
                {
                  key: 'tp',
                  label: 'TP手续费转给用户',
                  children: canList ? (
                    <div>
                      <div style={{ maxWidth: 900 }}>
                        <div style={readRowGrey}>
                          <span
                            style={{
                              width: 200,
                              textAlign: 'right',
                              color: 'rgba(0,0,0,0.85)',
                              flexShrink: 0,
                            }}
                          >
                            TP手续费剩余
                          </span>
                          <Input
                            readOnly
                            value={tpPool?.remaining ?? '—'}
                            style={{
                              marginLeft: 8,
                              flex: 1,
                              maxWidth: 560,
                              backgroundColor: '#9F9F9F',
                              color: '#fff',
                              fontWeight: 500,
                              border: '1px solid #8c8c8c',
                            }}
                            size="large"
                          />
                        </div>
                        {tpPool?.display_source === 'php_mirror' && (
                          <Alert
                            type="warning"
                            showIcon
                            style={{ maxWidth: 900, marginBottom: 12 }}
                            message={
                              <>
                                当前数字来自 <code>php_uo_uo_config.commission</code> 镜像（与线网大表同步展示）。
                                真正 <strong>扣池发放</strong> 仍走库内 <code>uo_config.tp_handling_fee_pool</code>，目前
                                为 <strong>{tpPool.uo_config_remaining ?? '0'}</strong>。若发放报「池不足」，请把与线网
                                一致的手续费池余额写入该配置或跑同步脚本。
                              </>
                            }
                          />
                        )}
                        <Form
                          form={tpForm}
                          layout="horizontal"
                          colon={false}
                          requiredMark
                          style={{ maxWidth: 900 }}
                          initialValues={{ asset: 'tp' as TokenId }}
                          labelCol={parityFormLabel.labelCol}
                          wrapperCol={parityFormLabel.wrapperCol}
                          labelAlign="right"
                        >
                          <Form.Item
                            name="username"
                            label="用户登录账号"
                            rules={[{ required: true, message: '请输入用户登录账号' }]}
                            style={{ marginBottom: 18 }}
                          >
                            <Input
                              size="large"
                              allowClear
                              autoComplete="off"
                              placeholder="登录名"
                            />
                          </Form.Item>
                          <Form.Item
                            name="amount"
                            label="发放数量"
                            rules={[{ required: true, message: '请输入发放数量' }]}
                            style={{ marginBottom: 18 }}
                          >
                            <Input
                              size="large"
                              allowClear
                              inputMode="decimal"
                              placeholder="发放数量"
                            />
                          </Form.Item>
                          <Form.Item
                            name="asset"
                            label="币种"
                            rules={[{ required: true, message: '请选择币种' }]}
                            style={{ marginBottom: 22 }}
                          >
                            <Select
                              size="large"
                              options={[
                                { value: 'tp' as TokenId, label: 'TP' },
                              ]}
                            />
                          </Form.Item>
                        </Form>
                        <div
                          style={{
                            textAlign: 'center',
                            marginTop: 8,
                            marginBottom: 8,
                            paddingTop: 12,
                            borderTop: '1px solid #f0f0f0',
                          }}
                        >
                          <Button
                            type="primary"
                            onClick={() => void runLookup()}
                            loading={lookupLoading}
                            style={{ marginRight: 12, minWidth: 88, height: 32 }}
                            size="middle"
                          >
                            查询
                          </Button>
                          {canTrigger && (
                            <Button
                              type="primary"
                              onClick={() => void runTpPreview()}
                              loading={tpPreviewLoading}
                              style={{ minWidth: 88, height: 32 }}
                              size="middle"
                            >
                              发放
                            </Button>
                          )}
                        </div>
                        {lookedUp && (
                          <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.65)', marginBottom: 8 }}>
                            将发放给 <strong>ID {lookedUp.id}</strong>
                            {lookedUp.nickname ? `（${lookedUp.nickname}）` : ''}
                          </div>
                        )}
                        {!canTrigger && canList && (
                          <Alert
                            type="info"
                            showIcon
                            style={{ maxWidth: 900 }}
                            message="无平台分裂触发权限时仅可查询，不可发放"
                          />
                        )}
                        <p style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 8 }}>
                          与线网 HTML #commission
                          一致为 Node 库 <code>uo_config.tp_handling_fee_pool</code>；与 uo.uoConfig
                          大表 JSON 的 commission 字段不是同一套数。
                        </p>
                      </div>
                    </div>
                  ) : (
                    <Alert type="warning" message="无权限" />
                  ),
                },
                {
                  key: 'split',
                  label: '拆分',
                  children: canList ? (
                    <div>
                      {cycleState && (
                        <Alert
                          type={
                            cycleState.phase === 'ready_to_split'
                              ? 'success'
                              : cycleState.phase === 'guide_sales_active'
                                ? 'warning'
                                : 'info'
                          }
                          showIcon
                          style={{ marginBottom: 16, maxWidth: 900 }}
                          message={`周期阶段：${cycleState.phase} · 未成交指导销售 UO：${cycleState.open_guide_sell_uo}`}
                          description="流程：拆分测算 → 指导销售批量挂卖 → 挂卖清零 → 拆分执行。静态奖在指导销售成交后发放。"
                        />
                      )}
                      <div style={{ maxWidth: 900 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 18,
                            minHeight: 40,
                          }}
                        >
                          <span
                            style={{
                              width: 200,
                              textAlign: 'right',
                              flexShrink: 0,
                              paddingRight: 8,
                            }}
                          >
                            <span style={{ color: '#ff4d4f' }}>*</span> 拆分倍数
                          </span>
                          <Input
                            size="large"
                            value={triggerRatio}
                            onChange={(e) => setTriggerRatio(e.target.value)}
                            placeholder="请输入拆分倍数"
                            style={{ flex: 1, minWidth: 0 }}
                          />
                        </div>
                        <div
                          style={{
                            textAlign: 'center',
                            marginBottom: 20,
                            paddingTop: 16,
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 10,
                          }}
                        >
                          {canTrigger && (
                            <>
                              <Button
                                type="primary"
                                loading={previewLoading}
                                onClick={() => void runSplitPreview()}
                                disabled={!triggerRatio || isNaN(parseFloat(triggerRatio))}
                                size="middle"
                                style={{ minWidth: 120 }}
                              >
                                拆分测算
                              </Button>
                              <Tooltip title="未接旧 PHP uo.config/split2，请用线网或补接入">
                                <Button type="primary" size="middle" style={{ minWidth: 120 }} disabled>
                                  拆分前捐赠(只点1次)
                                </Button>
                              </Tooltip>
                              <Button
                                type="primary"
                                size="middle"
                                style={{ minWidth: 120 }}
                                disabled={!cycleState?.can_execute_split}
                                onClick={() => {
                                  if (!preview && !cycleState?.split_preview) {
                                    void message.info('请先完成「拆分测算」');
                                    return;
                                  }
                                  if (!cycleState?.can_execute_split) {
                                    void message.warning('指导销售挂卖尚未清零，不能拆分');
                                    return;
                                  }
                                  setConfirmVisible(true);
                                }}
                              >
                                拆分(不捐赠)
                              </Button>
                            </>
                          )}
                        </div>
                        <div
                          style={{
                            marginTop: 24,
                            marginBottom: 16,
                            paddingTop: 16,
                            borderTop: '1px solid #f0f0f0',
                            maxWidth: 900,
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 12 }}>0418版指导销售</div>
                          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <div style={{ marginBottom: 4, fontSize: 12 }}>100$ 账户保留金额</div>
                              <Input value={level1Retain} onChange={(e) => setLevel1Retain(e.target.value)} />
                            </div>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <div style={{ marginBottom: 4, fontSize: 12 }}>1000$ 账户保留金额</div>
                              <Input value={level2Retain} onChange={(e) => setLevel2Retain(e.target.value)} />
                            </div>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <div style={{ marginBottom: 4, fontSize: 12 }}>100$ 剩余净量低于出局</div>
                              <Input value={netWeight1} onChange={(e) => setNetWeight1(e.target.value)} />
                            </div>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <div style={{ marginBottom: 4, fontSize: 12 }}>1000$ 剩余净量低于出局</div>
                              <Input value={netWeight2} onChange={(e) => setNetWeight2(e.target.value)} />
                            </div>
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <div style={{ marginBottom: 4, fontSize: 12 }}>卖出币多少涨价</div>
                              <Input value={guideCoins} onChange={(e) => setGuideCoins(e.target.value)} />
                            </div>
                          </div>
                          {(guidePreview || cycleState?.guide_sales_calc) && (
                            <div
                              style={{
                                fontSize: 12,
                                color: 'rgba(0,0,0,0.65)',
                                marginBottom: 8,
                                lineHeight: 1.6,
                              }}
                            >
                              {(() => {
                                const g = guidePreview ?? cycleState?.guide_sales_calc;
                                if (!g) return null;
                                return (
                                  <>
                                    <div>
                                      来源：{g.source === 'php_live' ? '线网 PHP' : '本地镜像'} · 单价 {g.unit_price}
                                    </div>
                                    <div>
                                      过线 UO {g.sum_uo} · AP {g.ap} · 最终价 {g.bj} · 超卖 {g.ymc}
                                    </div>
                                    <div>
                                      100$ 过线 {g.interval_people1} 人 ({g.interval1}) · 1000$ 过线{' '}
                                      {g.interval_people2} 人 ({g.interval2})
                                    </div>
                                    <div>
                                      100$ 出局 {g.net_weight_people1} 人 ({g.net_weight_interval1}) · 1000$ 出局{' '}
                                      {g.net_weight_people2} 人 ({g.net_weight_interval2})
                                    </div>
                                    <div>
                                      待批量挂卖 {g.trigger_count ?? '—'} 户 · {g.trigger_sum_uo ?? '—'} UO ·{' '}
                                      {g.trigger_sum_ap ?? '—'} AP
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          <div style={{ textAlign: 'center', display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <Button loading={guideSalesLoading} onClick={() => void runGuideSalesPreview()}>
                              计算
                            </Button>
                            <Button
                              type="primary"
                              loading={guideSalesLoading}
                              disabled={!cycleState?.can_trigger_guide_sales}
                              onClick={() => void triggerGuideSales()}
                            >
                              指导销售
                            </Button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 16,
                            minHeight: 40,
                          }}
                        >
                          <span
                            style={{
                              width: 200,
                              textAlign: 'right',
                              flexShrink: 0,
                              paddingRight: 8,
                              color: 'rgba(0,0,0,0.85)',
                            }}
                          >
                            30天未登录人数
                          </span>
                          <Input
                            readOnly
                            placeholder="—"
                            size="large"
                            style={{ flex: 1, minWidth: 0, background: '#fafafa' }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 8,
                            minHeight: 40,
                          }}
                        >
                          <span
                            style={{
                              width: 200,
                              textAlign: 'right',
                              flexShrink: 0,
                              paddingRight: 8,
                              color: 'rgba(0,0,0,0.85)',
                            }}
                          >
                            30天未登录强制捐赠数
                          </span>
                          <Input
                            readOnly
                            placeholder="—"
                            size="large"
                            style={{ flex: 1, minWidth: 0, background: '#fafafa' }}
                          />
                        </div>
                        {!canTrigger && (
                          <Alert type="info" message="无「拆分触发」权限" style={{ marginBottom: 12 }} />
                        )}
                      </div>
                      <div
                        style={{
                          marginTop: 20,
                          paddingTop: 20,
                          borderTop: '1px solid #e8e8e8',
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 12,
                            fontSize: 15,
                            fontWeight: 600,
                            color: 'rgba(0,0,0,0.88)',
                          }}
                        >
                          分裂记录
                        </div>
                        {logTableEl}
                      </div>
                    </div>
                  ) : (
                    <Alert type="warning" message="无平台分裂读权限" />
                  ),
                },
              ]}
            />
          </div>
        </ConfigProvider>
        {sharedTpModal}
        {splitConfirmModal}
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>拆分</h2>
      <Tabs
        defaultActiveKey="tp"
        items={[
          {
            key: 'tp',
            label: 'TP手续费转给用户',
            children: canList ? (
              <div style={{ maxWidth: 520 }}>
                <Form form={tpForm} layout="vertical" initialValues={{ asset: 'tp' as TokenId }}>
                  <Form.Item label="TP手续费剩余" required={false}>
                    <Statistic
                      valueStyle={{ fontSize: 22, fontFamily: 'monospace' }}
                      value={tpPool?.remaining ?? '—'}
                    />
                    {tpPool?.display_source === 'php_mirror' && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={
                          <>
                            展示值为 <code>php_uo_uo_config.commission</code>；扣池仍以 uo_config 为{' '}
                            {tpPool.uo_config_remaining ?? '0'}。发放前请同步 <code>tp_handling_fee_pool</code>。
                          </>
                        }
                      />
                    )}
                    <span style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 4 }}>
                      与线网一致时请维护 uo_config「tp_handling_fee_pool」；无配置时会回退显示镜像
                      commission。线网 HTML #commission 与 uo.uoConfig JSON 的 commission 含义可能不同。
                    </span>
                  </Form.Item>
                  <Form.Item
                    name="username"
                    label="用户登录账号"
                    rules={[{ required: true, message: '请输入账号' }]}
                  >
                    <Input allowClear autoComplete="off" placeholder="登录名 / username" />
                  </Form.Item>
                  <Space>
                    <Button onClick={() => void runLookup()} loading={lookupLoading} type="default">
                      查询
                    </Button>
                    {lookedUp && (
                      <span style={{ color: 'rgba(0,0,0,0.65)' }}>
                        将发放给 <strong>ID {lookedUp.id}</strong>
                        {lookedUp.nickname ? `（${lookedUp.nickname}）` : ''}
                      </span>
                    )}
                  </Space>
                  <Form.Item
                    name="amount"
                    label="发放数量"
                    rules={[{ required: true, message: '请输入数量' }]}
                  >
                    <Input allowClear inputMode="decimal" placeholder="正数" />
                  </Form.Item>
                  <Form.Item
                    name="asset"
                    label="币种"
                    rules={[{ required: true, message: '请选择币种' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="请选择"
                      options={TOKEN_IDS.map((t) => ({
                        value: t,
                        label: `${TOKEN_META[t].symbol} — ${TOKEN_META[t].name} (${t})`,
                      }))}
                    />
                  </Form.Item>
                </Form>
                {canTrigger && (
                  <Space style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      loading={tpPreviewLoading}
                      onClick={() => void runTpPreview()}
                    >
                      发放（先预览）
                    </Button>
                  </Space>
                )}
                {!canTrigger && canList && (
                  <Alert type="info" showIcon message="无「拆分触发」权限时仅可查询池与账户，不可发放" />
                )}
              </div>
            ) : (
              <Alert type="warning" message="无平台分裂读权限" />
            ),
          },
          {
            key: 'split',
            label: '拆分',
            children: canList ? (
              canTrigger ? (
                <div>
                  <Alert
                    type="warning"
                    showIcon
                    message="手动分裂"
                    description="为所有活跃用户批量执行 SP → ORB。先预览再执行。"
                    style={{ marginBottom: 12, maxWidth: 560 }}
                  />
                  <Space wrap>
                    <Form.Item label="分裂池" style={{ marginBottom: 0 }}>
                      <Select
                        value={triggerPool}
                        onChange={(v) => setTriggerPool(v as typeof triggerPool)}
                        style={{ width: 200 }}
                        options={[
                          { value: 'sp1', label: 'SP1（分裂池一）' },
                          { value: 'sp2', label: 'SP2（分裂池二）' },
                          { value: 'sp3', label: 'SP3（分裂池三）' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label="分裂比例" style={{ marginBottom: 0 }}>
                      <Input
                        value={triggerRatio}
                        onChange={(e) => setTriggerRatio(e.target.value)}
                        style={{ width: 120 }}
                        placeholder="0.1"
                        suffix="(0–1)"
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      loading={previewLoading}
                      onClick={() => void runSplitPreview()}
                      disabled={!triggerRatio || isNaN(parseFloat(triggerRatio))}
                    >
                      预览分裂
                    </Button>
                  </Space>
                </div>
              ) : (
                <Alert type="info" message="无「拆分触发」权限" />
              )
            ) : (
              <Alert type="warning" message="无平台分裂读权限" />
            ),
          },
          {
            key: 'log',
            label: '分裂记录',
            children: canList ? logTableEl : null,
          },
        ]}
      />

      {sharedTpModal}
      {splitConfirmModal}
    </div>
  );
}
